import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { draftMapRequested, fetchFestivalMap } from '../../api/festivalMap';
import { loadFestivalMap, refreshMapIfChanged } from '../../lib/festivalOffline';
import { queryKeys } from '../../lib/query/queryKeys';
import { GC } from '../../lib/query/staleTimes';
import { FESTIVAL_MAPS_ENABLED } from '../../config/festivalMaps';

/**
 * The published map for an event, or null (no map → Find renders as today).
 *
 * Phone first: the map store (lib/map/mapStore.js) answers on a cold start with no
 * network, and a 0.2 s version check re-downloads only a map that changed. A null
 * result is stored too — it is the common case and refetching it aggressively would
 * poll for maps that don't exist.
 */
export function useFestivalMap(eventId) {
  // A dev draft (?draft=1) gets its own key and is always refetched, so the persisted
  // published payload can never answer for it and a rebuilt draft shows on reload.
  const draft = draftMapRequested();
  const queryClient = useQueryClient();
  const enabled = FESTIVAL_MAPS_ENABLED && Boolean(eventId);
  useEffect(() => {
    if (enabled && !draft) void refreshMapIfChanged(queryClient, eventId);
  }, [enabled, draft, eventId, queryClient]);
  return useQuery({
    queryKey: draft
      ? [...queryKeys.festivalMap.published(eventId), 'draft']
      : queryKeys.festivalMap.published(eventId),
    queryFn: () => (draft ? fetchFestivalMap(eventId) : loadFestivalMap(eventId)),
    enabled,
    // offlineFirst: with no service React Query would pause before the queryFn runs, and the stored copy is read IN the
    // queryFn (the map is no longer in the persisted cache to hydrate from). The queryFn reads the phone first anyway.
    networkMode: draft ? 'online' : 'offlineFirst',
    // Never refetched on its own (Sam's resume freeze, 2026-10-16). A newer edition arrives through refreshMapIfChanged's
    // setQueryData; a refetch only re-read the same map from the store as a NEW object, and a new scene3d is a full 3D
    // rebuild on the main thread (0.9 s on a phone, far longer at the Bowl) the moment the app came back after 20 min.
    // No structural sharing either: deep-comparing every seat costs a frame, and a payload is replaced whole or not at all.
    staleTime: draft ? 0 : Infinity,
    refetchOnWindowFocus: draft,
    refetchOnReconnect: draft,
    structuralSharing: draft,
    gcTime: draft ? 0 : GC.festival,
  });
}
